import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { SignOut, User, SignIn, UserPlus, SquaresFour, Exam, ShieldCheck } from "@phosphor-icons/react";
import ThemeToggle from "./ThemeToggle";

export default function TopBar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const authed = user && user !== false;

  return (
    <header className="sticky top-0 z-40 bg-background border-b border-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
        <Link
          to="/"
          data-testid="topbar-logo-link"
          className="flex items-center gap-3 group min-w-0"
        >
          <span className="shrink-0" data-testid="topbar-uep-logo">
            <img src="/uep-logo.png" alt="UEP Campus Gutenberg" className="h-11 sm:h-14 w-auto dark:hidden" />
            <img src="/uep-logo-white.png" alt="UEP Campus Gutenberg" className="h-11 sm:h-14 w-auto hidden dark:block" />
          </span>
          <span className="w-px self-stretch bg-foreground/30 hidden min-[420px]:block" aria-hidden="true" />
          <div className="leading-none min-w-0 hidden min-[420px]:block sm:block">
            <div className="text-base sm:text-lg font-bold text-foreground tracking-tight truncate">Voxlingua</div>
            <div className="overline mt-1 sm:mt-1.5 hidden sm:block">English Self-Assessment</div>
          </div>
        </Link>

        <nav className="flex items-center gap-2 sm:gap-3 shrink-0">
          {authed ? (
            <>
              {/* Dashboard */}
              <Link
                to="/dashboard"
                data-testid="topbar-dashboard-link"
                aria-label="Dashboard"
                title="Dashboard"
                className="neu-btn inline-flex items-center gap-2 text-sm hover:text-indigo w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2 justify-center"
              >
                <SquaresFour size={16} weight="duotone" />
                <span className="hidden sm:inline">Dashboard</span>
              </Link>

              {/* Take Test */}
              <Link
                to="/test"
                data-testid="topbar-test-link"
                aria-label="Take Test"
                title="Take Test"
                className="neu-btn inline-flex items-center gap-2 text-sm hover:text-indigo w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2 justify-center"
              >
                <Exam size={16} weight="duotone" />
                <span className="hidden sm:inline">Take Test</span>
              </Link>

              {/* Admin — visible only to admin users */}
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="topbar-admin-link"
                  aria-label="Admin"
                  title="Admin"
                  className="neu-btn inline-flex items-center gap-2 text-sm hover:text-indigo w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2 justify-center"
                >
                  <ShieldCheck size={16} weight="duotone" />
                  <span className="hidden sm:inline">Admin</span>
                </Link>
              )}

              {/* User name — hidden on mobile */}
              <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground px-3">
                <User size={16} weight="duotone" />
                <span data-testid="topbar-user-name">{user.name}</span>
              </div>

              <ThemeToggle />

              {/* Log out */}
              <button
                data-testid="topbar-logout-btn"
                onClick={async () => { await logout(); nav("/"); }}
                aria-label="Log out"
                title="Log out"
                className="neu-btn inline-flex items-center gap-2 text-sm hover:text-indigo w-10 h-10 sm:w-auto sm:h-auto sm:px-4 sm:py-2 justify-center"
              >
                <SignOut size={14} weight="duotone" />
                <span className="hidden sm:inline">Log out</span>
              </button>
            </>
          ) : (
            <>
              <ThemeToggle />

              {/* Log in */}
              <Link
                to="/login"
                data-testid="topbar-login-link"
                aria-label="Log in"
                title="Log in"
                className="neu-btn inline-flex items-center gap-2 text-sm hover:text-indigo w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2 justify-center"
              >
                <SignIn size={16} weight="duotone" />
                <span className="hidden sm:inline">Log in</span>
              </Link>

              {/* Create free account */}
              <Link
                to="/register"
                data-testid="topbar-register-link"
                aria-label="Create free account"
                title="Create free account"
                className="neu-btn-primary inline-flex items-center gap-2 text-sm w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2.5 justify-center"
              >
                <UserPlus size={16} weight="bold" />
                <span className="hidden sm:inline">Create free account</span>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
